import { useEffect, useState } from 'react'
import { isDesktop } from '../lib/platform.ts'
import { assinarZoom, definirZoom, zoomAtual, ZOOM_MAX, ZOOM_MIN, ZOOM_PASSO } from '../lib/zoom.ts'
import { SobreSecao } from './Sobre.tsx'
import { AudioEVideoSecao } from './AudioEVideo.tsx'
import { THEMES, useTheme, type ColorMode, type DarkVariant } from './theme.tsx'
import './preferences.css'

/**
 * Modal de Preferencias.
 *
 * Protótipo: `docs/design-slices/_shell.html:1368-1452` (840x600, nav lateral
 * com 11 secoes, `:3209`). A secao **Visual** e a unica com efeito real hoje —
 * ela liga os temas que os tokens ja expoem. As demais aparecem com o aviso de
 * que ainda nao tem conteudo, em vez de sumirem do menu: o protótipo as lista.
 */

/** Secoes na ordem do protótipo (`_shell.html:3209`). */
const SECOES: readonly [string, string][] = [
  ['Disponibilidade', 'far fa-clock'],
  ['Notificações', 'far fa-bell'],
  ['Lista VIP', 'far fa-star'],
  ['Navegação', 'fas fa-bars'],
  ['Início', 'fas fa-home'],
  ['Visual', 'fas fa-paint-brush'],
  ['Mensagens e mídia', 'far fa-comment'],
  ['Idioma e região', 'fas fa-globe'],
  ['Acessibilidade', 'fas fa-universal-access'],
  ['Áudio e vídeo', 'fas fa-video'],
  ['Avançado', 'fas fa-sliders-h'],
  // Fora do protótipo, mas necessária: versão instalada + checagem de
  // atualização (pedido 2026-08-25).
  ['Sobre', 'fas fa-info-circle'],
]

const MODOS: readonly [ColorMode, string, string][] = [
  ['light', 'Claro', 'fas fa-sun'],
  ['dark', 'Escuro', 'fas fa-moon'],
  ['system', 'Sistema', 'fas fa-desktop'],
]

/** Modelos do escuro (SPEC-20261103-2330): o Black e uma variante, nao um modo. */
const MODELOS_DO_ESCURO: readonly [DarkVariant, string, string][] = [
  ['padrao', 'Escuro padrão', 'fas fa-moon'],
  ['black', 'Black (Claude)', 'fas fa-circle'],
]

export function PreferencesModal({ onClose }: { onClose: () => void }) {
  const [secao, setSecao] = useState('Visual')
  const { mode, setMode, darkVariant, setDarkVariant, theme, setTheme, primary, setPrimary } = useTheme()
  // Um zoom so, lembrado entre aberturas (SPEC-20261104-1534): a tela mostra o
  // vivo e acompanha Ctrl +/- feito com o modal aberto.
  const [zoom, setZoom] = useState(zoomAtual())
  useEffect(() => assinarZoom(setZoom), [])
  const trocarZoom = (pct: number) => setZoom(definirZoom(pct))

  const temaAtual = THEMES.find((t) => t.name === theme)

  return (
    <div className="prefs__scrim" role="presentation" onClick={onClose}>
      <div
        className="prefs"
        role="dialog"
        aria-modal="true"
        aria-label="Preferências"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="prefs__head">
          <h2 className="prefs__title">Preferências</h2>
          <button type="button" className="prefs__close" aria-label="Fechar" onClick={onClose}>
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        <div className="prefs__body">
          <nav className="prefs__nav" aria-label="Seções das preferências">
            {SECOES.map(([nome, icone]) => (
              <button
                key={nome}
                type="button"
                className={`prefs__navitem${secao === nome ? ' prefs__navitem--on' : ''}`}
                aria-current={secao === nome ? 'true' : undefined}
                onClick={() => setSecao(nome)}
              >
                <i className={icone} aria-hidden />
                {nome}
              </button>
            ))}
          </nav>

          <div className="prefs__content">
            {secao === 'Visual' ? (
              <>
                <section>
                  <h3 className="prefs__section">Modo de cor</h3>
                  <div className="prefs__modes">
                    {MODOS.map(([valor, rotulo, icone]) => (
                      <button
                        key={valor}
                        type="button"
                        className={`prefs__mode${mode === valor ? ' prefs__mode--on' : ''}`}
                        aria-pressed={mode === valor}
                        onClick={() => setMode(valor)}
                      >
                        <i className={icone} aria-hidden />
                        {rotulo}
                      </button>
                    ))}
                  </div>
                  {temaAtual?.highContrast ? (
                    <p className="prefs__hint">
                      O tema Alto contraste é sempre escuro — o modo de cor volta a valer ao escolher outro tema.
                    </p>
                  ) : null}
                </section>

                {/* o Black e um MODELO do escuro (SPEC-20261103-2330): aparece
                    quando o escuro pode valer (Escuro ou Sistema) */}
                {mode !== 'light' ? (
                  <section>
                    <h3 className="prefs__section">Modelo do escuro</h3>
                    <div className="prefs__modes">
                      {MODELOS_DO_ESCURO.map(([valor, rotulo, icone]) => (
                        <button
                          key={valor}
                          type="button"
                          className={`prefs__mode${darkVariant === valor ? ' prefs__mode--on' : ''}`}
                          aria-pressed={darkVariant === valor}
                          onClick={() => setDarkVariant(valor)}
                        >
                          <i className={icone} aria-hidden />
                          {rotulo}
                        </button>
                      ))}
                    </div>
                    <p className="prefs__hint">
                      {mode === 'system'
                        ? 'No modo Sistema, o modelo vale quando o sistema operacional estiver no escuro.'
                        : 'Black é o cinza neutro quase preto do Claude; com o tema "Claude" fica igual ao aplicativo.'}
                    </p>
                  </section>
                ) : null}

                <section>
                  <h3 className="prefs__section">Temas da Orbind</h3>
                  <div className="prefs__themes">
                    {THEMES.map((def) => (
                      <button
                        key={def.name}
                        type="button"
                        className={`prefs__theme${theme === def.name ? ' prefs__theme--on' : ''}`}
                        aria-pressed={theme === def.name}
                        onClick={() => setTheme(def.name)}
                      >
                        <span
                          className="prefs__swatch"
                          style={{ background: def.swatch ?? (def.custom ? primary : def.primary) }}
                          aria-hidden
                        />
                        <span className="prefs__themename">{def.name}</span>
                        {theme === def.name ? <i className="fas fa-check" aria-hidden /> : null}
                      </button>
                    ))}
                  </div>
                </section>

                {temaAtual?.custom ? (
                  <section>
                    <h3 className="prefs__section">Cor primária</h3>
                    <div className="prefs__custom">
                      <input
                        type="color"
                        value={primary}
                        onChange={(event) => setPrimary(event.target.value)}
                        aria-label="Escolher a cor primária"
                      />
                      <code>{primary}</code>
                    </div>
                    <p className="prefs__hint">
                      Todos os tons (hover, fundos suaves, bordas e halo de foco) são derivados desta cor.
                    </p>
                  </section>
                ) : null}

                {isDesktop() ? (
                <section>
                  <h3 className="prefs__section">Zoom da interface</h3>
                  <div className="prefs__zoom">
                    <button
                      type="button"
                      className="prefs__zoombtn"
                      aria-label="Diminuir zoom"
                      disabled={zoom <= ZOOM_MIN}
                      onClick={() => trocarZoom(zoom - ZOOM_PASSO)}
                    >
                      <i className="fas fa-minus" aria-hidden />
                    </button>
                    <span className="prefs__zoomval">{zoom}%</span>
                    <button
                      type="button"
                      className="prefs__zoombtn"
                      aria-label="Aumentar zoom"
                      disabled={zoom >= ZOOM_MAX}
                      onClick={() => trocarZoom(zoom + ZOOM_PASSO)}
                    >
                      <i className="fas fa-plus" aria-hidden />
                    </button>
                    {zoom !== 100 ? (
                      <button type="button" className="prefs__zoomreset" onClick={() => trocarZoom(100)}>
                        Restaurar 100%
                      </button>
                    ) : null}
                  </div>
                  <p className="prefs__hint">
                    Tamanho da interface (100% é o normal). O Orbind lembra o último zoom que você definiu e
                    abre nele, inclusive ao atualizar a tela. O conteúdo se ajusta à janela e mantém a
                    proporção ao navegar. Você também pode ajustar a qualquer hora com <kbd>Ctrl</kbd>{' '}
                    <kbd>+</kbd> / <kbd>Ctrl</kbd> <kbd>−</kbd> (ou <kbd>Ctrl</kbd> + roda do mouse), e voltar
                    a 100% com <kbd>Ctrl</kbd> <kbd>0</kbd>.
                  </p>
                </section>
                ) : null}
              </>
            ) : secao === 'Áudio e vídeo' ? (
              <AudioEVideoSecao />
            ) : secao === 'Sobre' ? (
              <SobreSecao />
            ) : (
              <div className="prefs__empty">
                <i className={SECOES.find(([nome]) => nome === secao)?.[1] ?? 'fas fa-cog'} aria-hidden />
                <h3>{secao}</h3>
                <p>Esta seção existe no protótipo e ainda não tem conteúdo implementado.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
